// PD Countdown
(function() {
  class CountdownTimer {
    constructor(element) {
      this.element = element;
      this.endDate = element.getAttribute('data-countdown-end');
      this.autoHide = element.getAttribute('data-auto-hide') === 'true';
      this.interval = null;
      this.isEnded = false;
      this.isFastScrolling = false;
      this.lastUpdateTime = 0;

      if (this.endDate) {
        this.targetDate = new Date(this.endDate);
        this.init();
      }
    }

    init() {
      // Parse date as Sydney time (AEDT/AEST)
      // Convert the input date to Sydney timezone
      this.targetDate = this.parseSydneyTime(this.endDate);

      // Validate date
      if (isNaN(this.targetDate.getTime())) {
        console.warn('Invalid countdown date format. Use YYYY-MM-DD HH:MM (Sydney time)');
        return;
      }

      // Start countdown
      this.updateCountdown();
      this.interval = setInterval(() => this.updateCountdown(), 1000);
    }

    parseSydneyTime(dateString) {
      try {
        // Parse the input date string and treat it as Sydney time
        // Use a more robust approach with proper timezone handling

        // First, parse the date string assuming it's in Sydney timezone
        // We'll create a date object and then adjust for Sydney timezone
        const inputDate = new Date(dateString);

        // If the browser supports Intl API, use it for accurate timezone conversion
        if (typeof Intl !== 'undefined' && Intl.DateTimeFormat) {
          // Create a date in Sydney timezone
          const sydneyFormatter = new Intl.DateTimeFormat('en-AU', {
            timeZone: 'Australia/Sydney',
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
          });

          // Get current Sydney time to determine offset
          const now = new Date();
          const sydneyNow = new Date(now.toLocaleString('en-US', { timeZone: 'Australia/Sydney' }));
          const localNow = new Date(now.toLocaleString('en-US'));
          const sydneyOffset = sydneyNow.getTime() - localNow.getTime();

          // Apply the Sydney offset to our input date
          return new Date(inputDate.getTime() - sydneyOffset);
        } else {
          // Fallback for browsers without Intl support
          // Use manual offset calculation
          const isDST = this.isSydneyDST(inputDate);
          const offset = isDST ? 11 : 10; // Sydney is UTC+11 during DST, UTC+10 otherwise
          const localOffset = inputDate.getTimezoneOffset() / 60; // Local timezone offset in hours
          const totalOffset = offset + localOffset; // Total offset to apply

          return new Date(inputDate.getTime() - (totalOffset * 60 * 60 * 1000));
        }
      } catch (error) {
        console.warn('Error parsing Sydney time:', error);
        return new Date(dateString); // Fallback to regular parsing
      }
    }

    isSydneyDST(date) {
      // More accurate DST check for Sydney
      // DST in Sydney runs from first Sunday in October to first Sunday in April
      const year = date.getFullYear();
      const month = date.getMonth(); // 0-based (0 = January)

      // DST period: October (month 9) to March (month 2) of next year
      if (month >= 9 || month <= 2) {
        return true; // Likely DST period
      } else if (month >= 4 && month <= 8) {
        return false; // Definitely not DST
      } else {
        // March or April - need more precise calculation
        // For simplicity, assume DST ends in early April
        return month === 2; // March
      }
    }

    updateCountdown() {
      // Skip updates during fast scrolling to prevent flickering
      if (this.isFastScrolling) {
        return;
      }

      const now = new Date().getTime();
      const distance = this.targetDate.getTime() - now;

      if (distance < 0) {
        this.handleCountdownEnd();
        return;
      }

      // Calculate time units
      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      // Update display with throttling for seconds to prevent flickering
      this.updateTimeUnit('days', days);
      this.updateTimeUnit('hours', hours);
      this.updateTimeUnit('minutes', minutes);

      // Throttle seconds updates to prevent rapid changes during scroll
      const currentTime = Date.now();
      if (currentTime - this.lastUpdateTime > 500) { // Update seconds every 500ms max
        this.updateTimeUnit('seconds', seconds);
        this.lastUpdateTime = currentTime;
      }
    }

    updateTimeUnit(unit, value) {
      const tensDigit = Math.floor(value / 10);
      const onesDigit = value % 10;

      const tensElement = this.element.querySelector(`[data-time-unit="${unit}"][data-digit="tens"]`);
      const onesElement = this.element.querySelector(`[data-time-unit="${unit}"][data-digit="ones"]`);

      // Only update if values have changed to prevent unnecessary DOM manipulation
      if (tensElement && tensElement.textContent !== tensDigit.toString()) {
        tensElement.textContent = tensDigit;
      }
      if (onesElement && onesElement.textContent !== onesDigit.toString()) {
        onesElement.textContent = onesDigit;
      }
    }

    handleCountdownEnd() {
      if (this.isEnded) return;

      this.isEnded = true;
      clearInterval(this.interval);

      // Set all numbers to 00
      ['days', 'hours', 'minutes', 'seconds'].forEach(unit => {
        this.updateTimeUnit(unit, 0);
      });

      // Auto-hide if enabled
      if (this.autoHide) {
        this.element.classList.add('pd-countdown--hidden');
        document.documentElement.style.setProperty('--header-sticky-top', 0);
      }

      document.documentElement.style.setProperty('--countdown-height', 0);

      // Dispatch custom event for potential custom handling
      this.element.dispatchEvent(new CustomEvent('countdownEnded', {
        detail: { countdown: this }
      }));
    }

    destroy() {
      if (this.interval) {
        clearInterval(this.interval);
      }
    }
  }

  // Initialize all countdown timers
  const countdownElements = document.querySelectorAll('.pd-countdown[data-countdown-end]');
  const countdownInstances = [];

  countdownElements.forEach(element => {
    const countdown = new CountdownTimer(element);
    countdownInstances.push(countdown);
  });

  // Fast scroll detection to prevent seconds flickering
  let isScrolling = false;
  let scrollTimeout;
  let lastScrollTop = 0;
  let scrollVelocity = 0;
  let lastScrollTime = 0;
  let isFastScrolling = false;
  let fastScrollTimeout;

  // Enhanced scroll event handler
  window.addEventListener('scroll', () => {
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const currentTime = Date.now();
    const timeDelta = currentTime - lastScrollTime;
    const scrollDelta = Math.abs(scrollTop - lastScrollTop);

    // Calculate scroll velocity
    if (timeDelta > 0) {
      scrollVelocity = scrollDelta / timeDelta;
    }

    // Detect fast scrolling (threshold for mobile)
    const isStrongScroll = scrollVelocity > 1.5; // Lower threshold for mobile

    if (isStrongScroll && !isFastScrolling) {
      isFastScrolling = true;
      // Pause countdown updates during fast scrolling
      countdownInstances.forEach(countdown => {
        countdown.isFastScrolling = true;
      });
    }

    if (!isScrolling) {
      isScrolling = true;
      requestAnimationFrame(() => {
        isScrolling = false;
      });
    }

    // Clear existing timeouts
    clearTimeout(scrollTimeout);
    clearTimeout(fastScrollTimeout);

    // Handle fast scrolling timeout
    if (isFastScrolling) {
      fastScrollTimeout = setTimeout(() => {
        isFastScrolling = false;
        // Re-enable countdown after fast scrolling stops
        countdownInstances.forEach(countdown => {
          countdown.isFastScrolling = false;
        });
      }, 300); // Longer timeout for mobile
    }

    // Regular scroll handling
    scrollTimeout = setTimeout(() => {
      if (!isFastScrolling) {
        // Resume normal countdown updates
        countdownInstances.forEach(countdown => {
          if (!countdown.isFastScrolling) {
            countdown.lastUpdateTime = 0; // Reset throttling
          }
        });
      }
    }, isFastScrolling ? 100 : 200);

    lastScrollTop = scrollTop;
    lastScrollTime = currentTime;
  }, { passive: true });

  // Cleanup on page unload
  window.addEventListener('beforeunload', () => {
    countdownInstances.forEach(countdown => countdown.destroy());
  });
})();